import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";

import {
  getAllExamSchedules,
} from "../../api/examsschedule/examApi";
import {
  getAllExamSchedulesPortal,
} from "../../api/examsschedule/examPortalApi";
import { getAllClasses } from "../../api/student/studentApi";
import { getAllClassesForPortal } from "../../api/student/studentPortalApi";
import { getAllSubjects } from "../../api/subjects/subjectApi";
import { getAllSubjectsPortal } from "../../api/subjects/subjectPortalApi";
import { getAllAcademicYears } from "../../api/academic/academicYearApi";
import { getAllAcademicYearsPortal } from "../../api/academic/academicYearPortalApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import ExamFiltersBar from "../../components/examsschedule/ExamFiltersBar";
import ScheduleStatusBadge from "../../components/examsschedule/ScheduleStatusBadge";
import ExamTypeBadge from "../../components/examsschedule/ExamTypeBadge";
import { isFullProfile } from "../../utils/examsschedule/examConstants";
import {
  formatDate,
  pickList,
  toId,
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Card,
  CardContent,
  Table,
  Alert,
  EmptyState,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const ScheduleList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    academicYear: "",
    classId: "",
    subjectId: "",
    scheduleStatus: "",
    fromDate: "",
    toDate: "",
  });

  const toast = useToast();

  const examsPath = isPortal ? "/staff-panel/exam/exams" : "/exam/exams";
  const seatingPath = isPortal
    ? "/staff-panel/exam/seating"
    : "/exam/seating";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clsRes, subRes, yearRes] = await Promise.all([
          isPortal ? getAllClassesForPortal() : getAllClasses(),
          isPortal ? getAllSubjectsPortal() : getAllSubjects(),
          isPortal
            ? getAllAcademicYearsPortal({})
            : getAllAcademicYears({}),
        ]);
        if (cancelled) return;
        setClasses(pickList(clsRes, ["classes", "data"]));
        setSubjects(pickList(subRes, ["subjects", "data"]));
        const yearList = pickList(yearRes, ["academicYears", "years", "data"]);
        setYears(
          yearList.map((y) =>
            typeof y === "string"
              ? y
              : y.yearName || y.name || y.academicYear || ""
          ).filter(Boolean)
        );
      } catch (e) {
        // non-fatal — filters still usable without master lists
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const res = isPortal
        ? await getAllExamSchedulesPortal(params)
        : await getAllExamSchedules(params);
      setSchedules(pickList(res, ["schedules", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load schedules");
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [isPortal, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: "date",
      header: "Date",
      nowrap: true,
      render: (s) => formatDate(s.examDate),
    },
    {
      key: "exam",
      header: "Exam",
      render: (s) => {
        const exam = s.examId || {};
        return (
          <div>
            <div className="font-medium text-neutral-900 dark:text-neutral-100">
              {exam.name || "Exam"}
            </div>
            <div className="mt-0.5">
              <ExamTypeBadge type={exam.type} />
            </div>
          </div>
        );
      },
    },
    {
      key: "subject",
      header: "Subject",
      render: (s) => s.subjectId?.name || "—",
    },
    {
      key: "class",
      header: "Class",
      render: (s) => s.classId?.className || "—",
    },
    {
      key: "time",
      header: "Time",
      nowrap: true,
      render: (s) => (
        <span className="text-neutral-600 dark:text-neutral-300">
          {(s.startTime || "—") + " – " + (s.endTime || "—")}
        </span>
      ),
    },
    {
      key: "maxMarks",
      header: "Max",
      render: (s) => s.maxMarks ?? "—",
    },
    {
      key: "status",
      header: "Status",
      render: (s) => <ScheduleStatusBadge status={s.scheduleStatus} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (s) => {
        const exam = s.examId || {};
        const examId = toId(exam) || toId(s.examId);
        const full = isFullProfile(exam);
        const id = toId(s);

        return (
          <div className="flex items-center justify-end gap-2">
            {examId && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => navigate(`${examsPath}/${examId}`)}
                className="text-primary-900 dark:text-primary-300 hover:bg-primary-50"
              >
                Exam
              </Button>
            )}
            {full && canManage && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => navigate(`${seatingPath}/${id}`)}
                className="text-accent-600 hover:bg-accent-50"
              >
                Seating
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const tableRows = useMemo(
    () => schedules.map((s) => ({ ...s, id: toId(s) })),
    [schedules]
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Header & Navigation */}
      <div>
        <button
          type="button"
          onClick={() => navigate(examsPath)}
          className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors mb-2"
        >
          <ArrowLeft className="h-4 w-4" /> Back to exams
        </button>
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
          Exam Schedules
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Cross-exam schedule list with filters (class / subject / status / dates)
        </p>
      </div>

      {/* Shared Filter Bar */}
      <ExamFiltersBar
        filters={filters}
        onChange={setFilters}
        classes={classes}
        subjects={subjects}
        academicYears={years}
        showStatus
        showClass
        showSubject
        showAcademicYear
        showDateRange
      />

      {/* Alert */}
      

      {/* Table Data */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Calendar}
                title="No schedules match these filters"
                description="Try adjusting your filter options above."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleList;